import report from '../report.js';
import { compressImage as compressImageByLibrary } from '@/packages/image-compress/src/index.js';
import {
  loadImage,
  base64ToFile,
  calcMaxSize,
  blobUrlToFile,
  logTime,
  formatLogTime,
  formatFileSize,
  drawImageWithOrientationToCanvas
} from './imageHelpers.js';
import { isDev } from '../config.js';

/**
 * 仅在开发环境输出 compressImage 调试日志
 *
 * @param {...any} args - 需要输出到控制台的日志参数
 * @returns {void}
 */
function logCompressImageDebug(...args) {
  // 仅开发环境输出临时调试日志
  if (isDev) {
    // console.log(...args);
  }
}

/**
 * 输出压缩调试日志。
 * @param {Object} consoleOptions - 压缩调试日志配置
 * @param {string} title - 日志标题
 * @param {Object} data - 日志数据
 * @returns {void}
 */
function writeCompressConsole(consoleOptions, title, data = {}) {
  // 未传入调试配置时保持静默。
  if (!consoleOptions || typeof consoleOptions.console !== 'function') {
    return;
  }

  consoleOptions.console({
    page: consoleOptions.page || 'utils/lib/compressImage',
    method: consoleOptions.method || 'compressImage',
    title,
    data: Object.assign({}, consoleOptions.data || {}, data)
  });
}

/**
 * 创建压缩日志上下文，供成功和失败日志复用。
 * @param {string|File} imageSource - 图片来源。
 * @param {Object} options - 压缩选项。
 * @returns {Object} 压缩日志上下文。
 */
function createCompressLogContext(imageSource, options) {
  return {
    originalSizeBytes: imageSource instanceof File ? imageSource.size : 0,
    naturalWidth: 0,
    naturalHeight: 0,
    outputWidth: 0,
    outputHeight: 0,
    format: '未知',
    maxSizeMB: options.maxSizeMB === undefined ? 5 : options.maxSizeMB,
    forceNormalEncodeCompression: options.forceNormalEncodeCompression === true,
    orientation: options.orientation === undefined ? 1 : options.orientation,
    usedOrientationCanvas: false
  };
}

/**
 * 格式化图片尺寸。
 * @param {number} width - 图片宽度。
 * @param {number} height - 图片高度。
 * @returns {string} 图片尺寸文案。
 */
function formatCompressDimension(width, height) {
  return width && height ? width + 'x' + height : '未生成';
}

/**
 * 格式化图片 MB 大小。
 * @param {number} size - 图片字节数。
 * @returns {string} 图片 MB 大小文案。
 */
function formatCompressSizeMB(size) {
  return typeof size === 'number' && size > 0
    ? (size / 1024 / 1024).toFixed(2) + 'MB'
    : '未知';
}

/**
 * 获取压缩方案文案。
 * @param {Object} context - 压缩日志上下文。
 * @param {Object|null} result - 压缩结果。
 * @param {Error|null} error - 压缩错误。
 * @returns {string} 压缩方案文案。
 */
function getCompressSchemeText(context, result, error) {
  // 压缩失败且没有可用结果时，标记为未完成。
  if (error) {
    return '未完成';
  }

  return [
    context.usedOrientationCanvas ? '方向修正 Canvas' : '原图输入',
    result.sizeLimited ? '大小优先压缩' : '速度优先压缩',
    context.forceNormalEncodeCompression ? '强制正常编码' : '默认编码',
    result.compressionFallback ? '兜底结果' : '正常结果'
  ].join(' / ');
}

/**
 * 获取采用压缩方案的原因。
 * @param {Object} context - 压缩日志上下文。
 * @param {Object|null} result - 压缩结果。
 * @param {Error|null} error - 压缩错误。
 * @returns {string} 压缩原因说明。
 */
function getCompressReasonText(context, result, error) {
  var reasons = [];
  var maxSizeText = context.maxSizeMB ? context.maxSizeMB + 'MB' : '未限制';

  // 压缩失败时直接记录错误原因。
  if (error) {
    return '压缩失败：' + ((error && error.message) || String(error));
  }

  // 方向修正会先走 Canvas，再进入真正压缩。
  if (context.usedOrientationCanvas) {
    reasons.push('orientation=' + context.orientation + '，需要先进行方向修正');
  }

  // 强制正常编码会禁用底层阈值跳过和大像素跳过。
  if (context.forceNormalEncodeCompression) {
    reasons.push(
      'forceNormalEncodeCompression=true，强制进入正常编码，不跳过真正压缩'
    );
  }

  var firstSizeText = formatCompressSizeMB(
    result.firstBlob && result.firstBlob.size
  );
  var finalSizeText = formatCompressSizeMB(result.blob && result.blob.size);
  var compressedLargerText = result.compressedLarger
    ? formatCompressSizeMB(result.compressedLargerSize)
    : '0.00MB';

  // 压缩结果体积不小于原图且最终回退原图时，补充说明最终结果来源。
  if (result.usedOriginal) {
    reasons.push(
      '压缩后体积不小于原图，变大体积=' +
        compressedLargerText +
        '，最终使用原图'
    );
  }

  // 首次压缩结果超出体积上限时，底层会切换为大小优先。
  if (result.sizeLimited) {
    reasons.push(
      '首次压缩结果 ' +
        firstSizeText +
        ' 超出 maxSizeMB=' +
        maxSizeText +
        '，因此切换为大小优先压缩；最终结果 ' +
        finalSizeText
    );
  } else {
    reasons.push(
      '压缩结果 ' +
        finalSizeText +
        ' 未触发 maxSizeMB=' +
        maxSizeText +
        ' 的大小优先压缩'
    );
  }

  // 使用兜底结果时，补充具体兜底原因。
  if (result.compressionFallback) {
    var fallbackText =
      '使用兜底结果，fallbackReason=' + (result.fallbackReason || '未知');

    // 大像素保护兜底需要说明实际像素和上限。
    if (result.fallbackReason === 'largePixels') {
      fallbackText +=
        '，输出像素=' +
        result.pixels +
        '，JS 编码上限=' +
        result.maxJsEncodePixels;
    }

    reasons.push(fallbackText);
  }

  return reasons.join('；');
}

/**
 * 创建最终压缩日志数据。
 * @param {Object} context - 压缩日志上下文。
 * @param {Object|null} result - 压缩结果。
 * @param {number} duration - 压缩耗时，单位毫秒。
 * @param {Error|null} [error=null] - 压缩错误。
 * @returns {Object} 压缩日志数据。
 */
function createCompressConsoleData(context, result, duration, error = null) {
  var outputWidth = result ? result.width || context.outputWidth : 0;
  var outputHeight = result ? result.height || context.outputHeight : 0;
  var outputSize = result && result.blob ? result.blob.size : 0;

  return {
    压缩变化: {
      尺寸:
        formatCompressDimension(context.naturalWidth, context.naturalHeight) +
        ' -> ' +
        formatCompressDimension(outputWidth, outputHeight),
      体积:
        formatCompressSizeMB(context.originalSizeBytes) +
        ' -> ' +
        formatCompressSizeMB(outputSize),
      压缩后是否变大: Boolean(result && result.compressedLarger),
      压缩后变大体积:
        result && result.compressedLarger
          ? formatCompressSizeMB(result.compressedLargerSize)
          : '0.00MB'
    },
    压缩方案: getCompressSchemeText(context, result, error),
    压缩耗时: duration + 'ms',
    图片格式: (result && result.format) || context.format,
    压缩质量: result && result.quality !== undefined ? result.quality : '未知',
    采用这个压缩方案的原因: getCompressReasonText(context, result, error)
  };
}

/**
 * 将旧压缩质量系数转换为 @julylb/image-compress 使用的 1-100 质量值。
 *
 * @param {number} quality - 旧逻辑中的 canvas.toBlob 质量系数，通常为 0-1
 * @returns {number} 图片压缩库使用的整数质量值
 */
function normalizeLibraryQuality(quality) {
  var qualityNumber = Number(quality);

  // 非法质量值按旧逻辑默认质量 1 处理。
  if (!qualityNumber) {
    qualityNumber = 1;
  }

  // 小于等于 1 的值视为旧 canvas 质量系数，需要放大到百分制。
  if (qualityNumber <= 1) {
    return Math.max(1, Math.min(100, Math.round(qualityNumber * 100)));
  }

  return Math.max(1, Math.min(100, Math.round(qualityNumber)));
}

/**
 * 将旧 MIME 类型转换为 @julylb/image-compress 的格式参数。
 *
 * @param {string} type - 旧逻辑计算出的输出 MIME 类型
 * @returns {string} 图片压缩库使用的格式参数
 */
function getLibraryFormat(type) {
  // 旧逻辑只会输出 image/jpeg 或 image/png，其他情况按 PNG 兜底。
  if (type === 'image/jpeg') {
    return 'jpeg';
  }

  return 'png';
}

/**
 * 将 canvas 转成 Blob，作为新压缩库的输入源。
 *
 * @param {HTMLCanvasElement} canvas - 已完成旋转和尺寸处理的画布
 * @param {string} type - 需要输出的 MIME 类型
 * @param {AbortSignal} signal - 压缩中断信号
 * @param {number} quality - Canvas 导出质量
 * @returns {Promise<Blob>} 画布导出的 Blob
 */
function canvasToSourceBlob(canvas, type, signal, quality) {
  return new Promise(function (resolve, reject) {
    var settled = false;

    /**
     * 统一收口 Canvas Blob 导出结果。
     * @param {'resolve'|'reject'} settleType - 收口类型
     * @param {*} value - Blob 或错误对象
     * @returns {void}
     */
    function settle(settleType, value) {
      // 已经收口时，忽略后续 toBlob 或 abort 回调。
      if (settled) {
        return;
      }
      settled = true;

      // 中断监听只在本次导出期间有效。
      if (signal) {
        signal.removeEventListener('abort', onAbort);
      }

      // 导出失败时保持 Promise.reject。
      if (settleType === 'reject') {
        reject(value);
        return;
      }

      resolve(value);
    }

    /**
     * 处理 Canvas 导出期间的中断信号。
     * @returns {void}
     */
    function onAbort() {
      settle('reject', createCompressAbortError());
    }

    // 导出前已经中断时，不再启动 toBlob。
    if (signal && signal.aborted) {
      reject(createCompressAbortError());
      return;
    }

    // 导出期间收到中断信号时，尽快结束等待链路。
    if (signal) {
      signal.addEventListener('abort', onAbort, { once: true });
    }

    canvas.toBlob(
      function (blob) {
        // toBlob 可能因为浏览器异常返回空值。
        if (!blob) {
          settle('reject', new Error('图片压缩失败'));
          return;
        }

        settle('resolve', blob);
      },
      type,
      quality
    );
  });
}

/**
 * 创建压缩前尺寸处理结果，作为真正压缩失败时的兜底结果。
 * @param {Blob} blob 已完成尺寸或方向处理的 Blob。
 * @param {Object} params 压缩参数。
 * @param {number} params.canvasQuality Canvas 兜底导出质量。
 * @param {string} reason 兜底原因。
 * @returns {{blob: Blob, firstBlob: {size: number}, sizeLimited: boolean, width: number, height: number, compressionFallback: boolean, fallbackReason: string}} 兜底压缩结果。
 */
function createPreprocessFallbackCompressResult(blob, params, reason) {
  return {
    blob: blob,
    firstBlob: {
      size: blob ? blob.size : 0
    },
    sizeLimited: false,
    width: params.maxWidth,
    height: params.maxHeight,
    compressionFallback: true,
    fallbackReason: reason,
    format: getLibraryFormat(params.type),
    actualMimeType: blob ? blob.type : '',
    encoder: 'Native Canvas Resize Fallback',
    compressedSize: blob ? blob.size : 0,
    encodedSize: blob ? blob.size : 0,
    mode: 'fallback',
    quality: params.canvasQuality
  };
}

/**
 * 按短边目标反推图片等比缩放后的长边上限。
 * @param {number} naturalWidth - 图片原始宽度
 * @param {number} naturalHeight - 图片原始高度
 * @param {number} minShortSide - 目标短边长度
 * @returns {number} 能让短边满足目标的长边上限
 */
function calculateMaxLongSideByMinShortSide(
  naturalWidth,
  naturalHeight,
  minShortSide
) {
  var shortSide = Math.min(naturalWidth, naturalHeight);
  var longSide = Math.max(naturalWidth, naturalHeight);

  // 图片尺寸异常时，不参与旋转专用尺寸限制。
  if (!(shortSide > 0) || !(longSide > 0) || !(minShortSide > 0)) {
    return 0;
  }

  return Math.ceil((minShortSide * longSide) / shortSide);
}

/**
 * 创建压缩中断错误。
 * @returns {Error|DOMException} 中断错误对象
 */
function createCompressAbortError() {
  // 浏览器支持 DOMException 时，使用标准 AbortError。
  if (typeof DOMException === 'function') {
    return new DOMException('图片压缩已中断', 'AbortError');
  }

  var error = new Error('图片压缩已中断');
  error.name = 'AbortError';
  return error;
}

/**
 * 检查压缩中断信号。
 * @param {AbortSignal} signal - 压缩中断信号
 * @returns {void}
 */
function checkCompressAbortSignal(signal) {
  // 收到中断信号时，停止后续压缩流程。
  if (signal && signal.aborted) {
    throw createCompressAbortError();
  }
}

/**
 * 使用 @julylb/image-compress 压缩图片源。
 *
 * @param {string|Blob|File} imageSource - 压缩库输入源
 * @param {Object} params - 压缩参数
 * @param {string} params.type - 输出 MIME 类型
 * @param {number} params.quality - 旧逻辑中的压缩质量
 * @param {number} params.maxSizeMB - 输出目标体积上限，单位 MB
 * @param {number} params.maxWidth - 输出宽度上限
 * @param {number} params.maxHeight - 输出高度上限
 * @param {number} params.timeout - 单次压缩超时时间，单位毫秒
 * @param {AbortSignal} params.signal - 压缩中断信号
 * @param {number} params.concurrency - 底层压缩并发数
 * @param {Object} params.consoleOptions - 压缩调试日志配置
 * @param {boolean} params.forceNormalEncodeCompression - 是否强制走正常编码压缩
 * @param {boolean} params.useOriginalWhenCompressedLarger - 压缩后体积不小于原图时是否允许回退原图
 * @returns {Promise<{blob: Blob, firstBlob: {size: number}, sizeLimited: boolean, width: number, height: number}>} 压缩结果
 */
async function compressSourceByLibrary(imageSource, params) {
  var type = params.type;
  var quality = params.quality;
  var maxSizeMB = params.maxSizeMB;
  var maxWidth = params.maxWidth;
  var maxHeight = params.maxHeight;
  var format = getLibraryFormat(type);
  var libraryQuality = normalizeLibraryQuality(quality);
  var baseOptions = {
    maxWidth: maxWidth,
    maxHeight: maxHeight,
    format: format,
    quality: libraryQuality,
    maxSizeMB: maxSizeMB,
    mode: 'speed',
    timeout: params.timeout,
    signal: params.signal,
    concurrency: params.concurrency,
    forceNormalEncodeCompression: params.forceNormalEncodeCompression,
    useOriginalWhenCompressedLarger: params.useOriginalWhenCompressedLarger,
    consoleOptions: params.consoleOptions
  };
  var compressStartTime = +new Date();
  var result = await compressImageByLibrary(imageSource, baseOptions);
  logCompressImageDebug('[compressImage] compressImageByLibrary duration', {
    duration: new Date() - compressStartTime,
    format,
    quality: libraryQuality,
    maxWidth: baseOptions.maxWidth,
    maxHeight: baseOptions.maxHeight,
    maxSizeMB
  });
  var firstAttempt = result.attempts && result.attempts[0];
  var firstBlob = {
    size: firstAttempt ? firstAttempt.size : result.blob.size
  };

  return {
    blob: result.blob,
    firstBlob: firstBlob,
    sizeLimited: !!result.sizeLimited,
    width: result.width || maxWidth,
    height: result.height || maxHeight,
    compressionFallback: !!result.compressionFallback,
    fallbackReason: result.fallbackReason,
    attempts: result.attempts,
    format: result.format || format,
    actualMimeType: result.actualMimeType,
    encoder: result.encoder,
    mode: result.mode,
    quality: result.quality,
    compressedSize: result.compressedSize,
    encodedSize: result.encodedSize,
    usedOriginal: result.usedOriginal,
    compressedLarger: result.compressedLarger,
    compressedLargerSize: result.compressedLargerSize,
    pixels: result.pixels,
    maxJsEncodePixels: result.maxJsEncodePixels
  };
}

/**
 * 使用 @julylb/image-compress 压缩已完成旋转和尺寸处理的 canvas。
 *
 * @param {HTMLCanvasElement} canvas - 已完成旋转和尺寸处理的画布
 * @param {Object} params - 压缩参数
 * @param {string} params.type - 输出 MIME 类型
 * @param {number} params.quality - 旧逻辑中的压缩质量
 * @param {number} params.maxSizeMB - 输出目标体积上限，单位 MB
 * @param {number} params.timeout - 单次压缩超时时间，单位毫秒
 * @param {AbortSignal} params.signal - 压缩中断信号
 * @param {number} params.concurrency - 底层压缩并发数
 * @param {Object} params.consoleOptions - 压缩调试日志配置
 * @param {boolean} params.forceNormalEncodeCompression - 是否强制走正常编码压缩
 * @param {boolean} params.useOriginalWhenCompressedLarger - 压缩后体积不小于原图时是否允许回退原图
 * @param {number} [params.canvasQuality=1] - Canvas 导出质量
 * @returns {Promise<{blob: Blob, firstBlob: {size: number}, sizeLimited: boolean, width: number, height: number}>} 压缩结果
 */
async function compressCanvasByLibrary(canvas, params) {
  var canvasQuality =
    params.canvasQuality === undefined ? 1 : params.canvasQuality;
  var sourceBlob = await canvasToSourceBlob(
    canvas,
    params.type,
    params.signal,
    canvasQuality
  );

  var sourceParams = {
    type: params.type,
    quality: params.quality,
    maxSizeMB: params.maxSizeMB,
    maxWidth: canvas.width,
    maxHeight: canvas.height,
    timeout: params.timeout,
    signal: params.signal,
    concurrency: params.concurrency,
    canvasQuality: canvasQuality,
    forceNormalEncodeCompression: params.forceNormalEncodeCompression,
    useOriginalWhenCompressedLarger: params.useOriginalWhenCompressedLarger,
    consoleOptions: params.consoleOptions
  };

  try {
    return await compressSourceByLibrary(sourceBlob, sourceParams);
  } catch (error) {
    return createPreprocessFallbackCompressResult(
      sourceBlob,
      sourceParams,
      error && error.name === 'AbortError' ? 'abort' : 'error'
    );
  }
}

/**
 * 将图片压到指定最大边长、可选 EXIF 方向修正，并使用图片压缩库返回 Blob URL（部分分支原样返回 data/blob 字符串）。
 *
 * @param {string|File} imageSource - 图片来源：URL、blob URL、data URL 字符串，或 File（与 loadImage 一致）
 * @param {Object} [options] - 压缩与行为选项
 * @param {number} [options.maxWidthOrHeight] - 长边上限（像素）。未传时在函数内会按 5000 参与缩放计算
 * @param {number} [options.quality=1] - canvas.toBlob 质量系数 0–1，体积超限递归时会乘以 0.9
 * @param {number} [options.maxSizeMB=5] - 输出 blob 目标体积上限（MB）；首次速度优先压缩超限时，会再执行一次大小优先压缩
 * @param {number} [options.repeatTime=0] - 历史递归轮次字段，仅保留调试日志兼容
 * @param {number} [options.orientation=1] - EXIF orientation 1–8；非 1 时会走画布变换
 * @param {string} [options.customImageType] - 历史字段，当前不参与输出格式判断
 * @param {boolean} [options.keepOriginType=false] - 历史字段，当前不参与压缩策略判断
 * @param {boolean} [options.autoRotateImage=false] - 为 true 且 orientation 为 1 时，按模板比例与横竖图自动改为 orientation=6
 * @param {number} [options.templateImageRatio=0.5] - 模板宽高比（宽/高），配合 autoRotateImage
 * @param {number} [options.rotateMinShortSide] - 自动旋转导出时的短边目标（像素），未旋转时不生效
 * @param {boolean} [options.keepWidthAndHeight=false] - 历史字段，当前大小优先压缩不改变已计算的输出尺寸
 * @param {string} [options.originImageType] - 'png'|'jpg'|'jpeg'，映射为 toBlob 的 MIME；缺省为 png
 * @param {number} [options.timeout=8000] - 底层压缩超时时间，单位毫秒
 * @param {AbortSignal} [options.signal] - 压缩中断信号
 * @param {number} [options.concurrency=1] - 底层压缩并发数
 * @param {boolean} [options.forceNormalEncodeCompression=false] - 是否强制走正常编码压缩
 * @param {boolean} [options.useOriginalWhenCompressedLarger=false] - 压缩后体积不小于原图时是否允许回退原图
 * @param {Object} [options.consoleOptions] - 压缩调试日志配置
 * @returns {Promise<string>} 通常为 blob: URL；base64/blob 源在体积与方向均无需处理时可能直接返回原 imageSource 字符串
 */
export async function compressImage(imageSource, options) {
  options = options || {};
  var time = +new Date();
  var compressLogContext = createCompressLogContext(imageSource, options);

  try {
    return await compressImageCore(
      imageSource,
      options,
      time,
      compressLogContext
    );
  } catch (error) {
    writeCompressConsole(
      options.consoleOptions,
      '压缩失败',
      createCompressConsoleData(
        compressLogContext,
        null,
        new Date() - time,
        error
      )
    );
    throw error;
  }
}

/**
 * 执行图片压缩核心流程。
 * @param {string|File} imageSource - 图片来源。
 * @param {Object} options - 压缩与行为选项。
 * @param {number} time - 压缩开始时间戳。
 * @param {Object} compressLogContext - 压缩日志上下文。
 * @returns {Promise<string>} 压缩后的图片地址。
 */
async function compressImageCore(
  imageSource,
  options,
  time,
  compressLogContext
) {
  // ---------- 1. 解析 options（默认值与历史字段） ----------
  var maxWidthOrHeight = options.maxWidthOrHeight;
  var quality = options.quality === undefined ? 1 : options.quality;
  var maxSizeMB = options.maxSizeMB === undefined ? 5 : options.maxSizeMB;
  var repeatTime = options.repeatTime === undefined ? 0 : options.repeatTime;
  var orientation = options.orientation === undefined ? 1 : options.orientation;
  var autoRotateImage =
    options.autoRotateImage === undefined ? false : options.autoRotateImage;
  var templateImageRatio =
    options.templateImageRatio === undefined ? 0.5 : options.templateImageRatio;
  var rotateMinShortSide = Math.floor(Number(options.rotateMinShortSide) || 0);
  var keepWidthAndHeight =
    options.keepWidthAndHeight === undefined
      ? false
      : options.keepWidthAndHeight;
  var timeout = options.timeout === undefined ? 8000 : options.timeout;
  var signal = options.signal;
  var concurrency = options.concurrency === undefined ? 1 : options.concurrency;
  var forceNormalEncodeCompression =
    options.forceNormalEncodeCompression === true;
  var useOriginalWhenCompressedLarger =
    options.useOriginalWhenCompressedLarger === true;
  var consoleOptions = options.consoleOptions;

  // ---------- 2. 加载图片，判断输入形态 ----------
  checkCompressAbortSignal(signal);
  var img = await loadImage(imageSource);
  checkCompressAbortSignal(signal);
  var isBase64Url = /^data:/.test(imageSource);
  var isBlobUrl = /^blob:/.test(imageSource);
  var rotate = 0;
  var naturalWidth = img.naturalWidth;
  var naturalHeight = img.naturalHeight;
  var originalSizeBytes = imageSource instanceof File ? imageSource.size : 0;
  compressLogContext.naturalWidth = naturalWidth;
  compressLogContext.naturalHeight = naturalHeight;
  compressLogContext.originalSizeBytes = originalSizeBytes;

  // ---------- 3. 可选：按模板比例自动旋转（仅原 orientation 为 1 时） ----------
  if (autoRotateImage && templateImageRatio && orientation == 1) {
    if (
      (naturalWidth > naturalHeight && templateImageRatio < 1) ||
      (naturalWidth < naturalHeight && templateImageRatio > 1)
    ) {
      orientation = 6;
    }
  }
  compressLogContext.orientation = orientation;

  // 仅在实际发生旋转时，才按短边目标反推旋转专用长边上限。
  if (autoRotateImage && orientation != 1 && rotateMinShortSide > 0) {
    var rotateMaxWidthOrHeight = calculateMaxLongSideByMinShortSide(
      naturalWidth,
      naturalHeight,
      rotateMinShortSide
    );
    // 旋转专用尺寸限制只负责缩小过大的图片，不放大小图。
    if (rotateMaxWidthOrHeight > 0) {
      rotateMaxWidthOrHeight = Math.min(
        rotateMaxWidthOrHeight,
        Math.max(naturalWidth, naturalHeight)
      );
    }

    // 已有长边上限来自调用方时，旋转逻辑只能进一步收紧，不能放大原有输出尺寸。
    if (maxWidthOrHeight && rotateMaxWidthOrHeight) {
      maxWidthOrHeight = Math.min(maxWidthOrHeight, rotateMaxWidthOrHeight);
    } else {
      maxWidthOrHeight = rotateMaxWidthOrHeight;
    }
  }

  // ---------- 4. 是否需要走 Canvas（缩放或方向修正） ----------
  var needCompress =
    maxWidthOrHeight &&
    (naturalWidth > maxWidthOrHeight || naturalHeight > maxWidthOrHeight);

  needCompress = needCompress || !!rotate || orientation != 1;

  // ---------- 5. 已是 data/blob 且体积与方向均无需处理则直接返回原串 ----------
  if (isBase64Url) {
    var file0 = await base64ToFile(imageSource);
    originalSizeBytes = file0.size;
    compressLogContext.originalSizeBytes = originalSizeBytes;
    // 与历史实现一致：此处用 file 字节与 maxSizeMB 比较（调用方若传 MB 需注意）
    if (
      file0.size <= maxSizeMB &&
      !needCompress &&
      !forceNormalEncodeCompression
    ) {
      return imageSource;
    }
  }
  if (isBlobUrl) {
    var file1 = await blobUrlToFile(imageSource);
    originalSizeBytes = file1.size;
    compressLogContext.originalSizeBytes = originalSizeBytes;
    var mb0 = file1.size / 1024 / 1024;
    if (mb0 <= maxSizeMB && !needCompress && !forceNormalEncodeCompression) {
      return imageSource;
    }
  }

  // ---------- 6. 输出 MIME（仅 originImageType；customImageType 不参与 toBlob） ----------
  var imageTypeMap = {
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg'
  };

  var type = imageTypeMap[options.originImageType] || imageTypeMap.png;
  compressLogContext.format = getLibraryFormat(type);

  // ---------- 7. 计算绘制到画布上的逻辑宽高（等比限制在长边 maxWidthOrHeight 内） ----------
  var canvasWidth = naturalWidth;
  var canvasHeight = naturalHeight;

  if (!maxWidthOrHeight) {
    maxWidthOrHeight = 5000;
  }
  if (naturalWidth > maxWidthOrHeight || naturalHeight > maxWidthOrHeight) {
    var res = calcMaxSize(
      canvasWidth,
      canvasHeight,
      maxWidthOrHeight,
      maxWidthOrHeight
    );
    canvasWidth = res.width;
    canvasHeight = res.height;
  }

  var compressResult;
  var outputWidth = canvasWidth;
  var outputHeight = canvasHeight;
  compressLogContext.outputWidth = outputWidth;
  compressLogContext.outputHeight = outputHeight;
  compressLogContext.usedOrientationCanvas = orientation != 1;

  // ---------- 8. 仅在需要方向修正时绘制到离屏 canvas ----------
  if (orientation != 1) {
    var canvas = document.createElement('canvas');
    try {
      drawImageWithOrientationToCanvas(
        canvas,
        img,
        orientation,
        canvasWidth,
        canvasHeight,
        naturalWidth,
        naturalHeight
      );
      compressResult = await compressCanvasByLibrary(canvas, {
        type: type,
        quality: quality,
        maxSizeMB: maxSizeMB,
        timeout: timeout,
        signal: signal,
        concurrency: concurrency,
        forceNormalEncodeCompression: forceNormalEncodeCompression,
        useOriginalWhenCompressedLarger: useOriginalWhenCompressedLarger,
        consoleOptions: null,
        canvasQuality: 0.8
      });
      outputWidth = canvas.width;
      outputHeight = canvas.height;
      compressLogContext.outputWidth = outputWidth;
      compressLogContext.outputHeight = outputHeight;
    } finally {
      canvas.width = 0;
      canvas.height = 0;
    }
  } else {
    compressResult = await compressSourceByLibrary(imageSource, {
      type: type,
      quality: quality,
      maxSizeMB: maxSizeMB,
      maxWidth: canvasWidth,
      maxHeight: canvasHeight,
      timeout: timeout,
      signal: signal,
      concurrency: concurrency,
      forceNormalEncodeCompression: forceNormalEncodeCompression,
      useOriginalWhenCompressedLarger: useOriginalWhenCompressedLarger,
      consoleOptions: null
    });
  }

  // ---------- 9. 使用图片压缩库导出 Blob 并生成临时 URL ----------
  var blob = compressResult.blob;
  var blobUrl = URL.createObjectURL(blob);
  var originalSizeMB = originalSizeBytes
    ? (originalSizeBytes / 1024 / 1024).toFixed(2)
    : null;
  var compressedSizeMB = blob ? (blob.size / 1024 / 1024).toFixed(2) : '0.00';
  var compressedWidth = compressResult.width || outputWidth;
  var compressedHeight = compressResult.height || outputHeight;
  var compressRate =
    originalSizeBytes && blob
      ? (((originalSizeBytes - blob.size) / originalSizeBytes) * 100).toFixed(2)
      : null;
  var finishTime = new Date() - time;
  writeCompressConsole(
    consoleOptions,
    '压缩完成',
    createCompressConsoleData(compressLogContext, compressResult, finishTime)
  );
  logCompressImageDebug(
    '[compressImage] compressed result\n' +
      '1. 压缩前：' +
      (originalSizeMB === null ? '未知 MB' : originalSizeMB + ' MB') +
      '，尺寸 ' +
      naturalWidth +
      'x' +
      naturalHeight +
      '；\n' +
      '2. 压缩后：' +
      compressedSizeMB +
      ' MB，尺寸 ' +
      compressedWidth +
      'x' +
      compressedHeight +
      '；\n' +
      '3. 大小压缩率：' +
      (compressRate === null ? '未知' : compressRate + '%') +
      '；\n' +
      '4. URL：' +
      blobUrl
  );
  logTime('compressImage耗时:' + finishTime + 'ms');

  // ---------- 10. 超过 maxSizeMB 时已由大小优先方案二次压缩；否则延迟上报体积 ----------
  if (maxSizeMB) {
    var blobSize = compressResult.firstBlob.size;
    var mb = blobSize / 1024 / 1024;

    // 首次速度优先压缩超限时，上报已切换为大小优先压缩。
    if (compressResult.sizeLimited) {
      report('util_compressImage', {
        time: formatLogTime(finishTime),
        mb: formatFileSize(blobSize),
        msg:
          '图片大小为' +
          Math.round(mb) +
          'mb,超过' +
          maxSizeMB +
          'mb，进行大小优先压缩'
      });
    }
  } else {
    setTimeout(function () {
      blobUrlToFile(blobUrl).then(function (file) {
        report('util_compressImage', {
          time: formatLogTime(finishTime),
          mb: formatFileSize(file.size)
        });
      });
    }, 1000);
  }
  return blobUrl;
}
